/**
 * P8 — Hot Leads Board (institutional spec).
 * Route: /admin/onboarding/leads
 *
 * - Premium dark table (Hybrid Elite: black + gold)
 * - KPIs at the top
 * - Drawer with full submission detail, AI brief, Assign / Pipeline / Add Task
 *   actions + direct CRM linkage
 * - Backend: GET /hot-leads, GET /lead/:id, POST /lead/:id/assign,
 *   PATCH /lead/:id/pipeline, POST /lead/:id/tasks
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Flame, Sparkles, Mail, Globe, Briefcase, Tag, Loader2, Filter, TrendingUp,
  Building2, User as UserIcon, ExternalLink, UserPlus, GitBranch, ListChecks,
  X, ArrowUpDown,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================ constants
const ROLE_BADGE = {
  client:   'bg-sky-500/15 text-sky-300 border border-sky-500/30',
  supplier: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
  partner:  'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  investor: 'bg-violet-500/15 text-violet-300 border border-violet-500/30',
  other:    'bg-slate-500/15 text-slate-300 border border-slate-500/30',
};

const PRIORITY_BADGE = {
  strategic: 'bg-rose-500 text-white',
  high:      'bg-orange-500 text-white',
  medium:    'bg-amber-300/30 text-amber-200 border border-amber-500/30',
  low:       'bg-slate-700/40 text-slate-300 border border-slate-500/30',
};

const ENTITY_ICON = { company: Building2, individual: UserIcon, organization: Globe };

const PIPELINE_STAGES = [
  { v: 'issued',      l: 'Issued' },
  { v: 'qualified',   l: 'Qualified' },
  { v: 'proposal',    l: 'Proposal' },
  { v: 'negotiation', l: 'Negotiation' },
  { v: 'won',         l: 'Won' },
  { v: 'lost',        l: 'Lost' },
  { v: 'on_hold',     l: 'On hold' },
];

const PIPELINE_BADGE = {
  issued:      'bg-slate-500/15 text-slate-300 border border-slate-500/30',
  qualified:   'bg-sky-500/15 text-sky-300 border border-sky-500/30',
  proposal:    'bg-violet-500/15 text-violet-300 border border-violet-500/30',
  negotiation: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
  won:         'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
  lost:        'bg-rose-500/20 text-rose-300 border border-rose-500/40',
  on_hold:     'bg-slate-500/15 text-slate-400 border border-slate-500/30',
};

const THRESHOLDS = [
  { v: '50', l: '≥ 50 (warm)' },
  { v: '70', l: '≥ 70 (hot)' },
  { v: '85', l: '≥ 85 (strategic)' },
];


// ============================================================ component
const HotLeadsBoard = () => {
  const { toast } = useToast();
  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  }), []);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState('70');
  const [sortBy, setSortBy] = useState('score');

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [lead, setLead] = useState(null);

  // Action dialogs
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEmail, setAssignEmail] = useState('');
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/hot-leads`, {
        ...auth, params: { min_score: Number(minScore), limit: 200 },
      });
      setList(r.data || []);
    } catch (e) {
      toast({ title: 'Could not load Hot Leads', description: e?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [auth, minScore, toast]);

  useEffect(() => { load(); }, [load]);

  const sortedList = useMemo(() => {
    const arr = [...list];
    if (sortBy === 'score') {
      arr.sort((a, b) => (b.ai_potential_score || 0) - (a.ai_potential_score || 0));
    } else if (sortBy === 'recent') {
      arr.sort((a, b) => (b.reviewed_at || '').localeCompare(a.reviewed_at || ''));
    } else if (sortBy === 'name') {
      arr.sort((a, b) => (a.legal_name || '').localeCompare(b.legal_name || ''));
    }
    return arr;
  }, [list, sortBy]);

  const stats = useMemo(() => {
    const total = list.length;
    const avg = total ? Math.round(list.reduce((s, x) => s + (x.ai_potential_score || 0), 0) / total) : 0;
    const strategic = list.filter(x => x.ai_priority === 'strategic').length;
    const top = list.length > 0 ? list.reduce(
      (acc, x) => (x.ai_potential_score || 0) > (acc.ai_potential_score || 0) ? x : acc, list[0],
    ) : null;
    return { total, avg, strategic, top };
  }, [list]);

  // -------------------------------------------------------- drawer
  const openLead = useCallback(async (id) => {
    setDrawerOpen(true); setDrawerLoading(true); setLead(null);
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/lead/${id}`, auth);
      setLead(r.data); setAssignEmail(r.data.assignee_email || '');
    } catch (e) {
      toast({ title: 'Could not open lead', description: e?.message, variant: 'destructive' });
    } finally { setDrawerLoading(false); }
  }, [auth, toast]);

  const refresh = useCallback(async () => {
    if (!lead) return;
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/lead/${lead.id}`, auth);
      setLead(r.data);
    } catch { /* ignore */ }
    await load();
  }, [auth, lead, load]);

  // -------------------------------------------------------- actions
  const submitAssign = useCallback(async () => {
    if (!lead || !assignEmail.trim()) return;
    setActing(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/onboarding/lead/${lead.id}/assign`,
        { assignee_email: assignEmail.trim() }, auth,
      );
      setLead(r.data);
      toast({ title: 'Assigned', description: `Lead now owned by ${assignEmail}.` });
      setAssignOpen(false);
      await load();
    } catch (e) {
      toast({ title: 'Assign failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setActing(false); }
  }, [auth, lead, assignEmail, toast, load]);

  const updatePipeline = useCallback(async (stage) => {
    if (!lead) return;
    setActing(true);
    try {
      const r = await axios.patch(
        `${API_URL}/api/onboarding/lead/${lead.id}/pipeline`,
        { stage }, auth,
      );
      setLead(r.data);
      toast({ title: 'Pipeline updated', description: `Stage = ${stage}.` });
      await load();
    } catch (e) {
      toast({ title: 'Pipeline update failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setActing(false); }
  }, [auth, lead, toast, load]);

  const submitTask = useCallback(async () => {
    if (!lead || !taskTitle.trim()) return;
    setActing(true);
    try {
      await axios.post(`${API_URL}/api/onboarding/lead/${lead.id}/tasks`, {
        title: taskTitle.trim(),
        description: taskDesc || null,
        due_at: taskDue || null,
        priority: taskPriority,
      }, auth);
      toast({ title: 'Task created' });
      setTaskOpen(false); setTaskTitle(''); setTaskDesc(''); setTaskDue(''); setTaskPriority('medium');
      await refresh();
    } catch (e) {
      toast({ title: 'Task creation failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setActing(false); }
  }, [auth, lead, taskTitle, taskDesc, taskDue, taskPriority, toast, refresh]);

  // ============================================================ render
  return (
    <div data-testid="hot-leads-page" className="text-white">
      {/* Header — Hybrid Elite */}
      <div className="mb-6 flex items-start gap-4 p-6 rounded-2xl border border-[#C9A86A]/30 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0A0A0A 0%,#1F1B12 60%,#0A0A0A 100%)' }}>
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(201,168,106,0.4), transparent 55%)' }} />
        <div className="relative w-12 h-12 rounded-xl bg-[#C9A86A]/15 border border-[#C9A86A]/40 flex items-center justify-center">
          <Flame className="w-6 h-6 text-[#C9A86A]" />
        </div>
        <div className="relative flex-1">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">Grupo BMP · CRM Pipeline</div>
          <h1 className="font-serif text-3xl text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Hot Leads Board
          </h1>
          <p className="text-white/55 text-sm mt-1">
            Approved registrations with AI potential ≥ threshold — assign owners, advance pipeline, dispatch tasks.
          </p>
        </div>
        <div className="relative flex items-center gap-2">
          <Filter className="w-4 h-4 text-white/40" />
          <Select value={minScore} onValueChange={setMinScore}>
            <SelectTrigger className="w-44 bg-black/40 border-white/15 text-white" data-testid="hot-leads-threshold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THRESHOLDS.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="hot-leads-stats">
        <KpiTile label="Hot leads"     value={stats.total} icon={Flame} />
        <KpiTile label="Average score" value={`${stats.avg}/100`} icon={TrendingUp} />
        <KpiTile label="Strategic"     value={stats.strategic} icon={Sparkles} />
        <KpiTile label="Top score"     value={stats.top ? `${Math.round(stats.top.ai_potential_score)}/100` : '—'} icon={Flame} />
      </div>

      {/* Premium dark table */}
      <div className="rounded-2xl border border-[#C9A86A]/20 overflow-hidden bg-gradient-to-b from-[#0A0A0A] to-[#15110A]">
        <div className="px-5 py-3 border-b border-[#C9A86A]/15 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">Pipeline · Approved Leads</div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-white/40" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 h-8 bg-transparent border-white/10 text-white/70 text-xs" data-testid="hot-leads-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="score">Score (desc)</SelectItem>
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="name">Legal name</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-white/50">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading leads…
          </div>
        ) : sortedList.length === 0 ? (
          <div className="p-12 text-center text-white/50" data-testid="hot-leads-empty">
            <Flame className="w-10 h-10 mx-auto mb-3 text-white/20" />
            No hot leads above the current threshold yet. Lower the bar or wait for new approved submissions.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="hot-leads-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-[0.18em] uppercase text-white/40 border-b border-white/5">
                  <th className="text-left px-5 py-2.5 w-[14%]">Score</th>
                  <th className="text-left px-3 py-2.5">Entity</th>
                  <th className="text-left px-3 py-2.5">Role · Pipeline</th>
                  <th className="text-left px-3 py-2.5">AI Brief</th>
                  <th className="text-left px-3 py-2.5">Owner</th>
                  <th className="text-right px-5 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedList.map((s) => {
                  const Icon = ENTITY_ICON[s.entity_type] || Building2;
                  const score = Math.round(s.ai_potential_score || 0);
                  return (
                    <tr key={s.id} data-testid={`hot-lead-${s.id}`}
                      className="border-b border-white/5 hover:bg-[#C9A86A]/5 transition-colors">
                      <td className="px-5 py-3.5">
                        <ScoreRing value={score} />
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-[#C9A86A]" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-white truncate">{s.legal_name}</div>
                            <div className="text-[10px] font-mono text-white/40">{s.submission_number}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex flex-wrap gap-1.5">
                          <Badge className={`${ROLE_BADGE[s.role] || ROLE_BADGE.other} text-[10px]`}>{(s.role || '').toUpperCase()}</Badge>
                          {s.current_pipeline_stage && (
                            <Badge className={`${PIPELINE_BADGE[s.current_pipeline_stage] || ''} text-[10px]`}>
                              <GitBranch className="w-2.5 h-2.5 inline mr-1" />{s.current_pipeline_stage}
                            </Badge>
                          )}
                          {s.ai_priority && (
                            <Badge className={`${PRIORITY_BADGE[s.ai_priority] || ''} text-[10px]`}>{s.ai_priority}</Badge>
                          )}
                          {s.documents_summary && s.documents_summary.required > 0 && (
                            <Badge
                              data-testid={`hot-lead-${s.id}-docs-badge`}
                              className={`text-[10px] ${
                                s.documents_summary.approved === s.documents_summary.required
                                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                  : 'bg-[#C9A86A]/15 text-[#C9A86A] border border-[#C9A86A]/30'
                              }`}>
                              Docs {s.documents_summary.approved}/{s.documents_summary.required}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3.5 max-w-md">
                        <p className="text-xs text-white/65 line-clamp-2 italic leading-relaxed">
                          {s.ai_executive_brief || '—'}
                        </p>
                      </td>
                      <td className="px-3 py-3.5">
                        {s.assignee_email ? (
                          <span className="text-xs text-[#C9A86A] font-medium">{s.assignee_email}</span>
                        ) : (
                          <span className="text-xs text-white/30">— unassigned —</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => openLead(s.id)} data-testid={`hot-lead-open-${s.id}`}
                          className="text-xs px-3 py-1.5 rounded-lg bg-[#C9A86A] hover:bg-[#B7945A] text-black font-semibold transition-colors">
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer — full lead detail */}
      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 bg-[#0A0A0A] border border-[#C9A86A]/30 text-white"
          data-testid="hot-lead-drawer">
          <div className="relative p-6 border-b border-[#C9A86A]/20"
            style={{ backgroundImage: 'linear-gradient(135deg,#0A0A0A,#1F1B12)' }}>
            <button onClick={() => setDrawerOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-white/70" />
            </button>
            {drawerLoading ? (
              <div className="text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
            ) : lead && (
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <ScoreRing value={Math.round(lead.ai_potential_score || 0)} large />
                  <div className="flex-1">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">{lead.submission_number}</div>
                    <DialogTitle className="font-serif text-2xl text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {lead.legal_name}
                    </DialogTitle>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Badge className={`${ROLE_BADGE[lead.role] || ROLE_BADGE.other} text-[10px]`}>{(lead.role || '').toUpperCase()}</Badge>
                      {lead.ai_priority && <Badge className={`${PRIORITY_BADGE[lead.ai_priority] || ''} text-[10px]`}>{lead.ai_priority}</Badge>}
                      {lead.ai_pipeline && (
                        <Badge className="bg-white/5 text-white/70 border border-white/10 text-[10px]">
                          <Briefcase className="w-2.5 h-2.5 inline mr-1" />{lead.ai_pipeline}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </DialogHeader>
            )}
          </div>

          {lead && (
            <div className="p-6 space-y-5">
              {/* AI brief */}
              {lead.ai_executive_brief && (
                <div className="p-4 rounded-xl bg-[#C9A86A]/8 border border-[#C9A86A]/30" data-testid="drawer-ai-brief">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#C9A86A] mb-1.5 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> AI executive brief
                  </div>
                  <p className="text-white/85 text-sm leading-relaxed italic">"{lead.ai_executive_brief}"</p>
                  {(lead.ai_tags || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {lead.ai_tags.map((t, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-black/40 border border-[#C9A86A]/30 text-[#E8D6AA]">
                          <Tag className="w-2.5 h-2.5 inline mr-1" />{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action bar */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="drawer-actions">
                <ActionButton onClick={() => setAssignOpen(true)} icon={UserPlus} label={lead.assignee_email ? 'Reassign' : 'Assign'} testId="drawer-assign-btn" />
                <PipelineMenu current={lead.current_pipeline_stage} onSelect={updatePipeline} disabled={acting} />
                <ActionButton onClick={() => setTaskOpen(true)} icon={ListChecks} label="Add task" testId="drawer-task-btn" />
              </div>

              {/* Identity grid */}
              <DetailGrid title="Identity">
                <DetailRow label="Entity" value={(lead.entity_type || '').toUpperCase()} />
                <DetailRow label="Tax ID / TRN" value={lead.tax_id || '—'} />
                <DetailRow label="Country" value={lead.country || '—'} />
                <DetailRow label="Website" value={lead.website || '—'} />
                <DetailRow label="Address" value={lead.address || '—'} full />
                <DetailRow label="Contact" value={`${lead.contact_name} · ${lead.contact_email}`} />
                <DetailRow label="Phone" value={lead.contact_phone || '—'} />
                <DetailRow label="Owner (lead)" value={lead.assignee_email || '— unassigned —'} />
              </DetailGrid>

              {/* CRM linkage */}
              {(lead.counterparty || lead.crm_deal) && (
                <DetailGrid title="CRM linkage">
                  {lead.counterparty && (
                    <DetailRow label="Counterparty" value={
                      <a href={`/admin/counterparties?focus=${lead.counterparty.id}`}
                        data-testid="drawer-cp-link"
                        className="text-[#C9A86A] hover:underline inline-flex items-center gap-1">
                        {lead.counterparty.legal_name} <ExternalLink className="w-3 h-3" />
                      </a>} />
                  )}
                  {lead.crm_deal && (
                    <DetailRow label="CRM Deal" value={
                      <a href={`/admin/crm/deals?focus=${lead.crm_deal.id}`}
                        data-testid="drawer-deal-link"
                        className="text-[#C9A86A] hover:underline inline-flex items-center gap-1">
                        {lead.crm_deal.deal_number} · {lead.crm_deal.stage} <ExternalLink className="w-3 h-3" />
                      </a>} />
                  )}
                </DetailGrid>
              )}

              {/* Tasks */}
              {(lead.tasks || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#C9A86A] mb-2">Tasks</div>
                  <ul className="space-y-1.5" data-testid="drawer-tasks">
                    {lead.tasks.map((t, i) => (
                      <li key={t.id || i}
                        className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-start gap-3">
                        <ListChecks className="w-4 h-4 text-[#C9A86A] mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-white/90 text-sm">{t.title}</div>
                          {t.description && <div className="text-xs text-white/55">{t.description}</div>}
                          <div className="text-[10px] text-white/40 mt-1 flex flex-wrap gap-2">
                            <span>Status: {t.status}</span>
                            <span>Priority: {t.priority}</span>
                            {t.due_at && <span>Due: {(t.due_at || '').slice(0, 10)}</span>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="px-6 py-4 border-t border-white/10 bg-black/40">
            <Button variant="ghost" onClick={() => setDrawerOpen(false)} className="text-white/70 hover:text-white">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Owner email</Label>
            <Input value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)}
              placeholder="someone@grupobmp.com" data-testid="assign-email-input" />
            <p className="text-[11px] text-slate-500">
              The linked CRM deal will be reassigned to the same owner.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={submitAssign} disabled={acting || !assignEmail.trim()} data-testid="assign-confirm-btn"
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task dialog */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="max-w-md" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>Add task to lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)}
                placeholder="e.g. Schedule kickoff call"
                data-testid="task-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={taskDesc} onChange={(e) => setTaskDesc(e.target.value)}
                data-testid="task-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due date</Label>
                <Input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)}
                  data-testid="task-due-input" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={taskPriority} onValueChange={setTaskPriority}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="strategic">Strategic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTaskOpen(false)}>Cancel</Button>
            <Button onClick={submitTask} disabled={acting || !taskTitle.trim()} data-testid="task-confirm-btn"
              className="bg-[#C9A86A] hover:bg-[#B7945A] text-black font-semibold">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


/* ============================================================ subcomponents */
const KpiTile = ({ label, value, icon: Icon }) => (
  <div className="p-4 rounded-xl border border-[#C9A86A]/20 bg-gradient-to-br from-[#0A0A0A] to-[#1A1610]">
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#C9A86A]/80">{label}</div>
      <Icon className="w-4 h-4 text-[#C9A86A]/70" />
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
  </div>
);

const ScoreRing = ({ value, large }) => {
  const size = large ? 80 : 52;
  const stroke = large ? 6 : 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;
  const color = value >= 85 ? '#E11D48' : value >= 70 ? '#EA580C' : '#C9A86A';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease-out' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-bold"
        style={{ color, fontSize: large ? 22 : 14 }}>
        {value}
      </div>
    </div>
  );
};

const ActionButton = ({ onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="w-full px-4 py-3 rounded-xl bg-white/5 hover:bg-[#C9A86A]/15 border border-white/10 hover:border-[#C9A86A]/40 transition-colors flex items-center justify-center gap-2 text-white/85">
    <Icon className="w-4 h-4 text-[#C9A86A]" />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

const PipelineMenu = ({ current, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} disabled={disabled}
        data-testid="drawer-pipeline-btn"
        className="w-full px-4 py-3 rounded-xl bg-white/5 hover:bg-[#C9A86A]/15 border border-white/10 hover:border-[#C9A86A]/40 transition-colors flex items-center justify-center gap-2 text-white/85 disabled:opacity-50">
        <GitBranch className="w-4 h-4 text-[#C9A86A]" />
        <span className="text-sm font-medium">
          Pipeline: <span className="text-[#C9A86A]">{current || 'set stage'}</span>
        </span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-[#0A0A0A] border border-[#C9A86A]/30 rounded-xl shadow-2xl overflow-hidden">
          {PIPELINE_STAGES.map((s) => (
            <button key={s.v}
              onClick={() => { onSelect(s.v); setOpen(false); }}
              data-testid={`drawer-pipeline-${s.v}`}
              className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                current === s.v
                  ? 'bg-[#C9A86A]/15 text-[#C9A86A]'
                  : 'text-white/80 hover:bg-white/5 hover:text-[#C9A86A]'
              }`}>
              {s.l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DetailGrid = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#C9A86A]/80 mb-2">{title}</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 p-4 rounded-xl bg-white/3 border border-white/10">
      {children}
    </div>
  </div>
);

const DetailRow = ({ label, value, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
    <div className="text-sm text-white/85">{value || '—'}</div>
  </div>
);

export default HotLeadsBoard;
